<template>
  <FormSubmitPopup
    :title="title"
    @popupClose="$emit('input', false)"
    v-model="value"
    :rightMethod="onSubmitted"
    :rightButtonText="$t('submit')"
    medium
  >
    <template v-slot:input>
      <v-container>
        <v-layout row wrap>
          <v-flex xs12 sm6>
            <v-autocomplete
              hide-details
              :label="$t('vendor')"
              class="required_field"
              :items="warehouses"
              item-text="warehouseName"
              :rules="[fieldIsRequired('warehouse')]"
              return-object
              v-model="warehouse"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              hide-details
              :label="$t('category')"
              class="required_field"
              :items="categories"
              item-text="displayName"
              :rules="[fieldIsRequired('category')]"
              item-value="value"
              v-model="localSupport.category"
            ></v-autocomplete>
          </v-flex>
        </v-layout>

        <v-layout v-if="localSupport.category === 'Inbound'" :key="'inbound-' + localSupport.category">
          <v-flex xs12 sm4>
            <v-combobox
              hide-details
              class="required_field"
              :label="$t('trackingNumbers')"
              v-model="localSupport.trackings"
              :rules="[fieldIsRequired('trackings')]"
              chips
              multiple
              deletable-chips
              :key="trackingsKey"
            ></v-combobox>
            <div
              v-for="(tracking, index) in normalizedTrackingList(localSupport.trackings)"
              :key="'inbound-carrier-' + index"
              class="caption grey--text text--darken-1"
            >
              {{ tracking }} ({{ $t('client') }}: {{ clientLabel(tracking) }})
            </div>
          </v-flex>
          <v-flex xs12 sm4>
            <v-text-field
              hide-details
              class="required_field"
              label="UPC"
              v-model.trim="localSupport.upc"
              :rules="[fieldIsRequired('upc')]"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4>
            <v-text-field
              hide-details
              class="required_field"
              :label="$t('quantity')"
              v-model.number="localSupport.quantity"
              :rules="[
                fieldIsRequired('quantity'),
                fieldIsInteger('quantity'),
                fieldIsNoLessThanZero('quantity')
              ]"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout v-if="localSupport.category === 'outbound'">
          <v-flex xs3>
            <v-text-field
              hide-details
              class="required_field"
              :label="$t('shipment id')"
              v-model.trim="localSupport.shipmentId"
              :rules="[fieldIsRequired('shipmentId')]"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout
          v-if="isSingleTrackingCategory"
          :key="'single-tracking-' + localSupport.category"
        >
          <v-flex xs12>
            <v-text-field
              hide-details
              class="required_field"
              :label="$t('trackingNumber')"
              v-model.trim="trackingInput"
              :rules="[fieldIsRequired('trackingNumber')]"
            ></v-text-field>
            <div v-if="trackingInput" class="caption grey--text text--darken-1">
              {{ $t('client') }}: {{ clientLabel(trackingInput) }}
            </div>
          </v-flex>
        </v-layout>

        <v-layout
          v-if="localSupport.category === 'RETURN_RESEND'"
          :key="'return-resend-' + localSupport.category"
          row
          wrap
        >
          <v-flex xs12>
            <v-layout
              v-for="(pair, index) in resendPairsLocal"
              :key="'resend-pair-' + index"
              row
              wrap
              align-end
            >
              <v-flex xs12 sm5 pr-2>
                <v-text-field
                  hide-details
                  class="required_field"
                  :label="$t('trackingNumber') + ' (Old)'"
                  v-model.trim="pair.oldTracking"
                  :rules="[fieldIsRequired('old tracking number'), pairOldUniqueRule(index)]"
                ></v-text-field>
                <div class="caption grey--text text--darken-1">
                  {{ $t('client') }}: {{ clientLabel(pair.oldTracking) }}
                </div>
              </v-flex>
              <v-flex xs12 sm5>
                <v-text-field
                  hide-details
                  class="required_field"
                  :label="$t('trackingNumber') + ' (New)'"
                  v-model.trim="pair.newTracking"
                  :rules="[fieldIsRequired('new tracking number'), pairNewUniqueRule(index)]"
                ></v-text-field>
                <div class="caption grey--text text--darken-1">
                  {{ $t('client') }}: {{ clientLabel(pair.newTracking) }}
                </div>
              </v-flex>
              <v-flex xs12 sm2 class="text-sm-right">
                <v-btn small icon @click="addResendPairRow">
                  <v-icon>add</v-icon>
                </v-btn>
                <v-btn
                  small
                  icon
                  :disabled="resendPairsLocal.length <= 1"
                  @click="removeResendPairRow(index)"
                >
                  <v-icon>remove</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>

        <v-layout
          v-if="['INTERCEPT_REASSIGN', 'RETURN_DESTROY', 'DOWNLOAD_POD'].includes(localSupport.category)"
          :key="'trackings-' + localSupport.category"
        >
          <v-flex xs12>
            <v-combobox
              hide-details
              class="required_field"
              :label="$t('trackingNumbers')"
              v-model="localSupport.trackings"
              :rules="[fieldIsRequired('trackings')]"
              chips
              multiple
              deletable-chips
              :key="trackingsKey"
            ></v-combobox>
            <div
              v-for="(tracking, index) in normalizedTrackingList(localSupport.trackings)"
              :key="'tracking-carrier-' + index"
              class="caption grey--text text--darken-1"
            >
              {{ tracking }} ({{ $t('client') }}: {{ clientLabel(tracking) }})
            </div>
          </v-flex>
        </v-layout>

        <v-layout align-center v-if="showEstimatedPrice">
          <v-icon style="color: deepskyblue">info</v-icon>
          <v-flex>
            {{ $t('estimatedPricePerItemWithTotal', { price: estimatedUnitPrice, total: estimatedPrice }) }}
          </v-flex>
        </v-layout>

        <v-layout v-if="localSupport.category === 'labelClaim' || localSupport.category === 'labelChange'" :key="'label-' + localSupport.category">
          <v-flex xs12 sm6>
            <v-text-field
              hide-details
              class="required_field"
              :label="$t('trackingNumber')"
              v-model.trim="trackingInput"
              :rules="[fieldIsRequired('trackingNumber')]"
            ></v-text-field>
            <div v-if="trackingInput" class="caption grey--text text--darken-1">
              {{ $t('client') }}: {{ clientLabel(trackingInput) }}
            </div>
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              hide-details
              class="required_field"
              :label="$t('LabelID')"
              v-model.trim="localSupport.labelId"
              :rules="[fieldIsRequired('Label ID')]"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-text-field
          hide-details
          class="mb-2"
          :label="$t('title')"
          v-model.trim="localSupport.title"
          :rules="[fieldIsRequired('title')]"
        ></v-text-field>
        <v-textarea
          hide-details
          outline
          :label="$t('question')"
          v-model="localSupport.question"
          :rules="[fieldIsRequired('question')]"
          :counter="500"
          auto-grow
          rows="3"
        ></v-textarea>
        <v-layout align-center v-if="localSupport.category === 'Inbound'">
          <v-icon style="color: deepskyblue">info</v-icon><v-flex>Proof of order with tracking number is required if the case is about missing package or wrong units in package</v-flex>
        </v-layout>
        <v-layout align-center v-if="localSupport.category === 'Inbound'">
          <v-icon style="color: deepskyblue">info</v-icon><v-flex>如果是要调查包裹丢失，或者是包裹里面的货物不对， 请上传带有跟踪号的下单截图或者其他详细证明</v-flex>
        </v-layout>
        <template v-if="warehouse.warehouseKey === 'system' && localSupport.category === 'labelClaim'">
          <v-layout align-center>
            <v-icon style="color: deepskyblue">info</v-icon><v-flex>Please upload purchased invoice and package photos</v-flex>
          </v-layout>
          <v-layout align-center>
            <v-icon style="color: deepskyblue">info</v-icon><v-flex>请上传购买发票和包裹图片</v-flex>
          </v-layout>
        </template>
        <v-flex xs12>
          <v-layout align-center v-if="localSupport.category === 'labelChange'">
            <v-icon style="color: deepskyblue">info</v-icon><v-flex>{{ $t('appliedAdditionalFee') }}</v-flex>
          </v-layout>
          <UploadFileWidget
            v-model="attachments"
            :maxFiles="5"
            type="supports"
            :sizeLimit="5242880 * 4"
            :removeFilesUponUnmounted="removeFilesUponUnmounted"
          />
        </v-flex>
      </v-container>
    </template>
  </FormSubmitPopup>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import UploadFileWidget from './UploadFileWidget'
import {checkRules} from '@/utils/tools'

const SYSTEM_CASE_ESTIMATE_PRICE_MAP = {
  INTERCEPT_REASSIGN: 5,
  RETURN_RESEND: 11,
  RETURN_DESTROY: 11,
  DOWNLOAD_POD: 3
}

const CARRIER_LOOKUP_DELAY = 400

export default {
  name: 'SupportEdit',
  components: {FormSubmitPopup, UploadFileWidget},
  mixins: [checkRules],
  data () {
    return {
      localSupport: {
        trackings: [],
        resendPairs: []
      },
      warehouse: {},
      attachments: [],
      removeFilesUponUnmounted: true,
      trackingsKey: 0,
      trackingInput: '',
      resendPairsLocal: [{oldTracking: '', newTracking: ''}],
      clientByTracking: {},
      clientLoadingByTracking: {},
      clientLookupTimers: {},
      clientLookupRequestId: {}
    }
  },
  mounted () {
    // Support legacy tracking field - convert to trackings array
    let trackings = this.support.trackings || []

    // Handle legacy tracking field (string)
    if (this.support.tracking && !trackings.length) {
      trackings = [this.support.tracking]
    }

    // Normalize trackings to string array for v-combobox
    // If trackings contains objects with trackingNumber property, extract to strings
    trackings = this.normalizedTrackingList(trackings)

    let resendPairs = this.normalizeResendPairs(this.support.resendPairs)
    if (!resendPairs.length && this.support.category === 'RETURN_RESEND') {
      if (trackings.length >= 2) {
        resendPairs = [{oldTracking: trackings[0], newTracking: trackings[1]}]
      } else if (trackings.length === 1) {
        resendPairs = [{oldTracking: trackings[0], newTracking: ''}]
      }
    }

    this.localSupport = {
      ...this.support,
      trackings,
      resendPairs
    }
    this.warehouse = {...this.initWarehouse}

    this.resendPairsLocal = resendPairs.length ? resendPairs : [this.createEmptyResendPair()]

    // Initialize trackingInput from trackings array if it has only one item
    // (for categories that use single text input)
    if (this.localSupport.trackings && this.localSupport.trackings.length === 1) {
      this.trackingInput = this.localSupport.trackings[0]
    } else if (this.support.tracking && !this.localSupport.trackings.length) {
      // Fallback to legacy tracking field
      this.trackingInput = this.support.tracking
    }

    this.refreshClientLookups()
  },
  beforeDestroy () {
    Object.keys(this.clientLookupTimers).forEach((key) => {
      clearTimeout(this.clientLookupTimers[key])
    })
  },
  watch: {
    'warehouse.warehouseKey' (val) {
      if (val === 'system') {
        this.$set(this.localSupport, 'category', 'INTERCEPT_REASSIGN')
      } else {
        this.$set(this.localSupport, 'category', 'billing')
      }
    },
    'localSupport.category' (newVal, oldVal) {
      // Reset fields when switching categories to avoid stale data
      if (oldVal) {
        const trackingCategories = ['Inbound', 'INTERCEPT_REASSIGN', 'RETURN_RESEND', 'RETURN_DESTROY', 'DOWNLOAD_POD', 'LOST_DAMAGED_SIZE_CLAIM', 'labelClaim', 'labelChange']
        if (trackingCategories.includes(oldVal)) {
          this.$set(this.localSupport, 'trackings', [])
          this.trackingInput = ''
        }
        if (oldVal === 'RETURN_RESEND') {
          this.resendPairsLocal = [this.createEmptyResendPair()]
          this.$set(this.localSupport, 'resendPairs', [])
        }
        // Reset other category-specific fields
        if (oldVal === 'Inbound') {
          this.$set(this.localSupport, 'upc', '')
          this.$set(this.localSupport, 'quantity', '')
        }
        if (oldVal === 'outbound') {
          this.$set(this.localSupport, 'shipmentId', '')
        }
        if (oldVal === 'labelClaim' || oldVal === 'labelChange') {
          this.$set(this.localSupport, 'labelId', '')
        }
      }
      if (newVal === 'RETURN_RESEND' && this.resendPairsLocal.length < 1) {
        this.resendPairsLocal = [this.createEmptyResendPair()]
      }
      // Force re-render when entering any category that uses trackings combobox
      const comboboxTrackingCategories = ['Inbound', 'INTERCEPT_REASSIGN', 'RETURN_DESTROY', 'DOWNLOAD_POD']
      if (comboboxTrackingCategories.includes(newVal)) {
        this.trackingsKey++
      }
      this.refreshClientLookups()
    },
    trackingInput () {
      this.refreshClientLookups()
    },
    'localSupport.trackings': {
      deep: true,
      handler () {
        this.refreshClientLookups()
      }
    },
    resendPairsLocal: {
      deep: true,
      handler () {
        this.refreshClientLookups()
      }
    }
  },
  computed: {
    tenant () {
      return {
        tenantKey: this.$store.getters.activeOrganization || '',
        tenantName: this.$store.getters.tenant.name || ''
      }
    },
    warehouses () {
      return [...this.$store.getters.allPreferredWarehouses, {
        warehouseName: 'System',
        warehouseKey: 'system',
        orgId: 'system'
      }]
    },
    categories () {
      return this.warehouse.warehouseKey === 'system' ? [
        {
          displayName: this.$t('INTERCEPT_REASSIGN'),
          value: 'INTERCEPT_REASSIGN'
        }, {
          displayName: this.$t('RETURN_RESEND'),
          value: 'RETURN_RESEND'
        }, {
          displayName: this.$t('RETURN_DESTROY'),
          value: 'RETURN_DESTROY'
        }, {
          displayName: this.$t('DOWNLOAD_POD'),
          value: 'DOWNLOAD_POD'
        }, {
          displayName: this.$t('LOST_DAMAGED_SIZE_CLAIM'),
          value: 'LOST_DAMAGED_SIZE_CLAIM'
        }, {
          displayName: this.$t('OTHER'),
          value: 'OTHER'
        }
      ] : [
        {
          displayName: this.$t('billing'),
          value: 'billing'
        }, {
          displayName: this.$t('inbound'),
          value: 'Inbound'
        }, {
          displayName: this.$t('outbound'),
          value: 'outbound'
        }, {
          displayName: this.$t('others'),
          value: 'others'
        }
      ]
    },
    estimatedPrice () {
      if (this.warehouse.warehouseKey !== 'system') {
        return null
      }

      const category = this.localSupport.category
      const unitPrice = SYSTEM_CASE_ESTIMATE_PRICE_MAP[category]
      if (!unitPrice) {
        return null
      }

      if (category === 'RETURN_RESEND') {
        const trackingCount = this.normalizeResendPairs(this.resendPairsLocal)
          .filter(({oldTracking, newTracking}) => oldTracking && newTracking)
          .length
        return parseInt(unitPrice * trackingCount, 10)
      }

      if (['INTERCEPT_REASSIGN', 'RETURN_DESTROY', 'DOWNLOAD_POD'].includes(category)) {
        const trackingCount = this.normalizedTrackingList(this.localSupport.trackings).length
        return parseInt(unitPrice * trackingCount, 10)
      }

      return parseInt(unitPrice, 10)
    },
    showEstimatedPrice () {
      return Number.isInteger(this.estimatedPrice)
    },
    estimatedUnitPrice () {
      const category = this.localSupport.category
      return SYSTEM_CASE_ESTIMATE_PRICE_MAP[category] || null
    },
    isSingleTrackingCategory () {
      return this.localSupport.category === 'LOST_DAMAGED_SIZE_CLAIM'
    }
  },
  methods: {
    async onSubmitted () {
      const singleTrackingCategories = ['LOST_DAMAGED_SIZE_CLAIM', 'labelClaim', 'labelChange']

      let finalTrackings = this.normalizedTrackingList(this.localSupport.trackings)
      let finalResendPairs = []

      if (this.localSupport.category === 'RETURN_RESEND') {
        finalResendPairs = this.validateAndBuildResendPairs()
        finalTrackings = this.getFlattenTrackingsFromPairs(finalResendPairs)
      } else if (singleTrackingCategories.includes(this.localSupport.category)) {
        finalTrackings = this.trackingInput ? [String(this.trackingInput).trim()] : []
      }

      const supportData = {
        ...this.localSupport,
        trackings: this.normalizedTrackingList(finalTrackings),
        resendPairs: this.localSupport.category === 'RETURN_RESEND' ? finalResendPairs : []
      }

      const payload = this.$store.getters.activeOrganization ? {
        attachments: this.attachments,
        ...supportData,
        ...this.warehouse,
        ...this.tenant
      } : {
        attachments: this.attachments,
        ...supportData,
        tenantKey: 'system',
        tenantName: 'System',
        warehouseKey: this.$store.getters.activeWarehouse,
        warehouseName: this.$store.getters.warehouseLimitedInfo.warehouseName
      }
      this.removeFilesUponUnmounted = false
      try {
        await this.$store.dispatch('addSupport', payload)
      } catch (err) {
        console.error(err)
        this.$store.dispatch('showToast', { info: err.message || 'Failed to submit support case', level: 'error' })
        this.removeFilesUponUnmounted = true
        throw err
      }
    },
    createEmptyResendPair () {
      return {oldTracking: '', newTracking: ''}
    },
    normalizeTrackingKey (tracking) {
      return String(tracking || '').trim().toUpperCase()
    },
    normalizedTrackingList (trackings = []) {
      if (!Array.isArray(trackings)) {
        return []
      }
      const unique = new Set()
      const normalizedTrackings = []
      trackings.forEach(item => {
        let tracking = ''
        if (typeof item === 'string') {
          tracking = item
        } else if (item && item.trackingNumber) {
          tracking = item.trackingNumber
        }
        tracking = String(tracking || '').trim()
        if (!tracking) {
          return
        }
        const trackingKey = this.normalizeTrackingKey(tracking)
        if (!unique.has(trackingKey)) {
          unique.add(trackingKey)
          normalizedTrackings.push(tracking)
        }
      })
      return normalizedTrackings
    },
    normalizeResendPairs (pairs = []) {
      if (!Array.isArray(pairs)) {
        return []
      }
      return pairs
        .map(pair => ({
          oldTracking: String((pair && pair.oldTracking) || '').trim(),
          newTracking: String((pair && pair.newTracking) || '').trim()
        }))
        .filter(pair => pair.oldTracking || pair.newTracking)
    },
    addResendPairRow () {
      this.resendPairsLocal.push(this.createEmptyResendPair())
    },
    removeResendPairRow (index) {
      if (this.resendPairsLocal.length <= 1) {
        return
      }
      this.resendPairsLocal.splice(index, 1)
    },
    pairOldUniqueRule (currentIndex) {
      return (value) => {
        const trackingKey = this.normalizeTrackingKey(value)
        if (!trackingKey) {
          return true
        }
        const hasDuplicate = this.resendPairsLocal.some((pair, index) => {
          if (index === currentIndex) {
            return false
          }
          return this.normalizeTrackingKey(pair.oldTracking) === trackingKey
        })
        return !hasDuplicate || 'Old tracking number must be unique'
      }
    },
    pairNewUniqueRule (currentIndex) {
      return (value) => {
        const trackingKey = this.normalizeTrackingKey(value)
        if (!trackingKey) {
          return true
        }
        const hasDuplicate = this.resendPairsLocal.some((pair, index) => {
          if (index === currentIndex) {
            return false
          }
          return this.normalizeTrackingKey(pair.newTracking) === trackingKey
        })
        return !hasDuplicate || 'New tracking number must be unique'
      }
    },
    validateAndBuildResendPairs () {
      const normalizedPairs = this.normalizeResendPairs(this.resendPairsLocal)
      if (!normalizedPairs.length) {
        throw new Error('At least one tracking pair is required for RETURN_RESEND category')
      }

      const oldTrackingSet = new Set()
      const newTrackingSet = new Set()
      normalizedPairs.forEach((pair) => {
        if (!pair.oldTracking || !pair.newTracking) {
          throw new Error('Both old and new tracking numbers are required for RETURN_RESEND category')
        }

        const oldTrackingKey = this.normalizeTrackingKey(pair.oldTracking)
        const newTrackingKey = this.normalizeTrackingKey(pair.newTracking)

        if (oldTrackingSet.has(oldTrackingKey)) {
          throw new Error(`Duplicate old tracking number: ${pair.oldTracking}`)
        }
        if (newTrackingSet.has(newTrackingKey)) {
          throw new Error(`Duplicate new tracking number: ${pair.newTracking}`)
        }

        oldTrackingSet.add(oldTrackingKey)
        newTrackingSet.add(newTrackingKey)
      })

      return normalizedPairs
    },
    getFlattenTrackingsFromPairs (pairs = []) {
      const trackingKeySet = new Set()
      const flattenedTrackings = []
      pairs.forEach(({oldTracking, newTracking}) => {
        ;[oldTracking, newTracking].forEach((tracking) => {
          const trackingValue = String(tracking || '').trim()
          if (!trackingValue) {
            return
          }
          const trackingKey = this.normalizeTrackingKey(trackingValue)
          if (!trackingKeySet.has(trackingKey)) {
            trackingKeySet.add(trackingKey)
            flattenedTrackings.push(trackingValue)
          }
        })
      })
      return flattenedTrackings
    },
    getActiveTrackingNumbers () {
      const category = this.localSupport.category
      if (category === 'RETURN_RESEND') {
        const pairs = this.normalizeResendPairs(this.resendPairsLocal)
        return pairs
          .map(({oldTracking}) => String(oldTracking || '').trim())
          .filter(Boolean)
      }

      if (['LOST_DAMAGED_SIZE_CLAIM', 'labelClaim', 'labelChange'].includes(category)) {
        const tracking = String(this.trackingInput || '').trim()
        return tracking ? [tracking] : []
      }

      if (['Inbound', 'INTERCEPT_REASSIGN', 'RETURN_DESTROY', 'DOWNLOAD_POD'].includes(category)) {
        return this.normalizedTrackingList(this.localSupport.trackings)
      }

      return []
    },
    refreshClientLookups () {
      const activeTrackings = this.getActiveTrackingNumbers()
      const activeTrackingKeys = new Set(activeTrackings.map(tracking => this.normalizeTrackingKey(tracking)).filter(Boolean))

      activeTrackings.forEach((tracking) => {
        this.queueClientLookup(tracking)
      })

      Object.keys(this.clientByTracking).forEach((trackingKey) => {
        if (!activeTrackingKeys.has(trackingKey)) {
          this.$delete(this.clientByTracking, trackingKey)
        }
      })
      Object.keys(this.clientLoadingByTracking).forEach((trackingKey) => {
        if (!activeTrackingKeys.has(trackingKey)) {
          this.$delete(this.clientLoadingByTracking, trackingKey)
        }
      })
      Object.keys(this.clientLookupRequestId).forEach((trackingKey) => {
        if (!activeTrackingKeys.has(trackingKey)) {
          this.$delete(this.clientLookupRequestId, trackingKey)
        }
      })
      Object.keys(this.clientLookupTimers).forEach((trackingKey) => {
        if (!activeTrackingKeys.has(trackingKey)) {
          clearTimeout(this.clientLookupTimers[trackingKey])
          this.$delete(this.clientLookupTimers, trackingKey)
        }
      })
    },
    queueClientLookup (trackingNumber) {
      const tracking = String(trackingNumber || '').trim()
      if (!tracking) {
        return
      }
      const trackingKey = this.normalizeTrackingKey(tracking)
      if (this.clientByTracking[trackingKey] && this.clientByTracking[trackingKey] !== '--') {
        return
      }
      if (this.clientLoadingByTracking[trackingKey]) {
        return
      }
      if (this.clientLookupTimers[trackingKey]) {
        clearTimeout(this.clientLookupTimers[trackingKey])
      }

      this.$set(this.clientLoadingByTracking, trackingKey, true)
      this.$set(this.clientLookupTimers, trackingKey, setTimeout(() => {
        this.fetchClientForTracking(tracking)
      }, CARRIER_LOOKUP_DELAY))
    },
    async fetchClientForTracking (trackingNumber) {
      const tracking = String(trackingNumber || '').trim()
      if (!tracking) {
        return
      }
      const trackingKey = this.normalizeTrackingKey(tracking)
      const requestId = (this.clientLookupRequestId[trackingKey] || 0) + 1
      this.$set(this.clientLookupRequestId, trackingKey, requestId)

      try {
        const result = await this.$store.dispatch('getLabelByTracking', {
          trackingNumbers: [tracking]
        })
        const labels = result && Array.isArray(result.data) ? result.data : []
        const firstLabel = labels.length > 0 ? labels[0] : null
        const clientName = String((firstLabel && firstLabel.clientName) || '').trim() || '--'

        if (this.clientLookupRequestId[trackingKey] === requestId) {
          this.$set(this.clientByTracking, trackingKey, clientName)
        }
      } catch (err) {
        if (this.clientLookupRequestId[trackingKey] === requestId) {
          this.$set(this.clientByTracking, trackingKey, '--')
        }
      } finally {
        if (this.clientLookupRequestId[trackingKey] === requestId) {
          this.$set(this.clientLoadingByTracking, trackingKey, false)
        }
        if (this.clientLookupTimers[trackingKey]) {
          clearTimeout(this.clientLookupTimers[trackingKey])
          this.$delete(this.clientLookupTimers, trackingKey)
        }
      }
    },
    clientLabel (trackingNumber) {
      const trackingKey = this.normalizeTrackingKey(trackingNumber)
      if (!trackingKey) {
        return '--'
      }
      if (this.clientLoadingByTracking[trackingKey]) {
        return '...'
      }
      return this.clientByTracking[trackingKey] || '--'
    }
  },
  props: {
    value: Boolean,
    title: {
      type: String,
      default () {
        return this.$t('addSupportCase')
      }
    },
    support: {
      type: Object,
      default: () => {
        return {}
      }
    },
    initWarehouse: {
      type: Object,
      default () {
        return {
          warehouseKey: ''
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/SupportEdit.vue